import { datadogRum } from '@datadog/browser-rum';
import { PrimaryButton } from '@shared/components/buttons';
import { inputClass } from '@shared/components/form-styles';
import { Spinner } from '@shared/components/spinner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { uploaderUrl } from '../../components/download-uploader-button';
import { linkClassName } from '../../components/links';
import { Page } from '../../components/page';
import { AlertContext } from '../../contexts/alert-context';
import type { Store } from '../../data/store';
import { useStores } from '../../hooks/stores';
import { useSignupCompletion } from '../../hooks/use-signup-completion';
import { useStoreEventListener } from '../../hooks/use-store-event-listener';
import { storeQueryOptions } from '../../query/query-options';
import { authenticatedLayout } from '../../route-apis';
import { StoreWebSocketProvider } from '../../websocket/store-websocket-provider';

const defaultStoreName = (store: Store) => `${store.owner_first_name}'s Store`;

// Step state derived from store record
type StepState = 'complete' | 'active' | 'upcoming';

function deriveSteps(
  store: Store | null,
  checkoutSuccess: boolean,
  nameJustConfirmed: boolean
): {
  step1: StepState;
  step2: StepState;
  step3: StepState;
  step4: StepState;
  step5: StepState;
} {
  // checkoutSuccess only advances the flow when there is a store to advance.
  // On the bare /onboard route store is null, and steps 3-5 all need one — so
  // without this the page shows an active step 3 whose card cannot render.
  const isEnabled = store != null && (store.enabled || checkoutSuccess);
  const hasUploaded = store?.last_uploaded_at != null;
  const isNamed =
    store != null && (nameJustConfirmed || hasUploaded || store.name !== defaultStoreName(store));

  return {
    step1: 'complete', // Always complete — user is authenticated
    step2: isEnabled ? 'complete' : 'active',
    step3: !isEnabled ? 'upcoming' : !isNamed ? 'active' : 'complete',
    step4: !isEnabled || !isNamed ? 'upcoming' : !hasUploaded ? 'active' : 'complete',
    step5: hasUploaded ? 'active' : 'upcoming',
  };
}

// Step indicator circle
function StepCircle({ state, number }: { state: StepState; number: number }) {
  const config = {
    complete: {
      bg: 'bg-primary-700',
      text: 'text-white text-base',
      content: '\u2713',
    },
    active: {
      bg: 'bg-blue-500',
      text: 'text-white text-sm font-bold',
      content: String(number),
    },
    upcoming: {
      bg: 'bg-gray-200',
      text: 'text-gray-400 text-sm font-bold',
      content: String(number),
    },
  };
  const { bg, text, content } = config[state];

  return (
    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${bg} ${text}`}>
      {content}
    </div>
  );
}

// Connector line between steps
function StepConnector({ state }: { state: StepState }) {
  return (
    <div
      className={`w-0.5 flex-1 min-h-[32px] ${state === 'complete' ? 'bg-primary-700' : 'bg-gray-200'}`}
    />
  );
}

// Step 2 — Start your free trial (expanded content when active)
function PaymentStep({
  store,
  onStartTrial,
  submitting,
}: {
  store: Store | null;
  onStartTrial: () => void;
  submitting: boolean;
}) {
  const isRetry = store != null && !store.enabled;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 mt-3">
      {isRetry && (
        <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 text-base text-amber-800 mb-4">
          Your previous checkout wasn't completed. No charges were made.
        </div>
      )}
      <p className="text-base text-gray-700 mb-3 leading-relaxed">
        Your 60-day free trial starts today. No charge until the trial ends.
      </p>
      <p className="text-base text-gray-700 mb-5 leading-relaxed">
        Your price depends on your store's annual sales, which Orderbot reads from your data. A week
        before your trial ends, we'll email you your plan and price, with the option to pay yearly
        and get two months free.
      </p>
      <PrimaryButton onClick={onStartTrial} disabled={submitting} className="w-full">
        {submitting && <Spinner />}
        Continue to Payment
      </PrimaryButton>
      <p className="text-sm text-gray-500 text-center mt-2">
        You'll enter your payment details on the next screen
      </p>
    </div>
  );
}

// Step 3 — Tell us your store's name (expanded content when active)
function NameStep({
  store,
  onSave,
  submitting,
}: {
  store: Store;
  onSave: (newName: string) => void;
  submitting: boolean;
}) {
  const [name, setName] = useState(store.name);
  const trimmed = name.trim();
  const disabled = submitting || trimmed === '' || trimmed === store.name;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 mt-3">
      <p className="text-base text-gray-700 mb-3 leading-relaxed">
        This name appears in Orderbot and the Companion Windows app.
      </p>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className={`${inputClass} w-full mb-4`}
        placeholder="Your store name"
      />
      <PrimaryButton onClick={() => onSave(trimmed)} disabled={disabled} className="w-full">
        {submitting && <Spinner />}
        Save and continue
      </PrimaryButton>
    </div>
  );
}

// Step 4 — Import your data (expanded content when active).
// This only renders while step 4 is active, which is exactly when the upload
// poll is running — so the spinner is unconditional rather than taking an
// isPolling prop that was never false here.
function ImportStep({ userEmail }: { userEmail: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 mt-3">
      <p className="font-medium mb-2 text-base">
        Install the Orderbot Companion on your LiquorPOS computer:
      </p>
      <ol className="list-decimal ml-5 mb-4 space-y-1.5 text-base text-gray-700">
        <li>
          Download{' '}
          <a href={uploaderUrl} className={`${linkClassName} underline`}>
            Orderbot Companion
          </a>{' '}
          (requires Windows 10 or later)
        </li>
        <li>Install and open it on a computer running LiquorPOS</li>
        <li>
          Sign in as <span className="font-medium">{userEmail}</span>
        </li>
        <li>Select your LiquorPOS data and installation folders</li>
        <li>Click "Upload" to start your first import</li>
      </ol>
      <div className="bg-blue-50 rounded-md px-4 py-3 text-base text-blue-800">
        We'll take you to your dashboard as soon as your data is ready.
      </div>
      <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
        <Spinner />
        <span>Checking for new uploads...</span>
      </div>
    </div>
  );
}

// Step 5 — Redirect countdown
function RedirectStep({ storeId }: { storeId: string }) {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (countdown <= 0) {
      navigate({ to: '/stores/$storeId', params: { storeId } });

      return;
    }

    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);

    return () => clearTimeout(timer);
  }, [countdown, navigate, storeId]);

  return (
    <div className="bg-primary-50 border border-primary-200 rounded-md px-4 py-3 text-base text-primary-800 mt-3">
      Data detected! Redirecting to your dashboard in {countdown}s...
    </div>
  );
}

// Mounts the store WebSocket for the duration of step 4 so a completed upload
// arrives as a push instead of a 1s poll. Renders nothing: the socket context
// has no other consumer on this page, and the listener's cache invalidation is
// what the page actually reads.
//
// Must live inside the provider, hence a component rather than a hook call in
// Onboard itself.
function OnboardStoreEvents({ storeId }: { storeId: string }) {
  useStoreEventListener(storeId);

  return null;
}

export function Onboard({ storeId: storeIdParam }: { storeId?: string }) {
  useSignupCompletion();
  const { user, storeService, stripeService } = authenticatedLayout.useRouteContext();
  const { addErrorAlert } = useContext(AlertContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { stores, loading: storesLoading, updateStore } = useStores();

  const [submitting, setSubmitting] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [nameJustConfirmed, setNameJustConfirmed] = useState(false);
  const [savingName, setSavingName] = useState(false);

  // Parse ?checkout=success from URL. Both /onboard and /onboard/$storeId
  // declare the same validateSearch, so this is typed without a cast.
  const search = useSearch({ strict: false });
  useEffect(() => {
    if (search.checkout !== 'success') {
      return;
    }

    setCheckoutSuccess(true);

    // Strip the param. useNavigate() here is unbound — this component is
    // rendered by both onboard routes — so name the destination explicitly
    // rather than using the search-reducer form, which types to `never`.
    if (storeIdParam) {
      navigate({
        to: '/onboard/$storeId',
        params: { storeId: storeIdParam },
        search: { checkout: undefined },
        replace: true,
      });
    } else {
      navigate({ to: '/onboard', search: { checkout: undefined }, replace: true });
    }
  }, [search, navigate, storeIdParam]);

  // Find the relevant store. This reads the stores list only: the poll below
  // splices its result into that list, so a completed upload arrives here the
  // same way any other store update does — no second copy to keep in step.
  const store = useMemo(() => {
    if (!storeIdParam) {
      return null;
    }

    return stores.find((s) => s.id === storeIdParam) ?? null;
  }, [storeIdParam, stores]);

  const steps = deriveSteps(store, checkoutSuccess, nameJustConfirmed);

  // Wait for the first upload while step 4 is active.
  //
  // The query stays mounted either way — it is what OnboardStoreEvents'
  // invalidation refetches, and the splice below is what carries its result
  // into the list this page reads. Only the *interval* is conditional.
  //
  // Once the store is enabled the socket is up (see OnboardStoreEvents below)
  // and pushStoreUpdated fires on every completed upload, so polling is pure
  // waste — and this is the expensive window, minutes long while the user
  // downloads and first-runs Companion.
  //
  // Before that, across checkout → Stripe webhook, there is no socket to
  // listen on: authorizeStoreAccess refuses $connect for a store that is not
  // enabled. So that window keeps the 1s poll exactly as it was. It is the
  // short one, seconds rather than minutes.
  //
  // refetchInterval rather than a setInterval, because
  // refetchIntervalInBackground defaults to false: TanStack stops the interval
  // when the tab loses focus and resumes on return. Step 4 is the step a user
  // is most likely to walk away from — it waits on them installing Companion —
  // so a hand-rolled interval here is what hammers the API overnight.
  const isWaitingForUpload = steps.step4 === 'active' && storeIdParam != null;
  const socketCoversUpdates = store?.enabled === true && storeIdParam != null;
  const { data: polledStore, error: pollError } = useQuery({
    ...storeQueryOptions(storeIdParam ?? ''),
    queryFn: () => storeService.getById(storeIdParam ?? ''),
    refetchInterval: socketCoversUpdates ? false : 1_000,
    enabled: isWaitingForUpload,
  });

  useEffect(() => {
    if (pollError == null) {
      return;
    }

    // A failed tick is usually a network hiccup or the webhook race, and the
    // next tick covers it — so no alert. It was previously swallowed by a bare
    // `catch {}`, which also hid a genuinely broken upload path.
    datadogRum.addError(pollError, { source: 'onboard-upload-poll', storeId: storeIdParam });
  }, [pollError, storeIdParam]);

  // storeLayout.beforeLoad reads the stores list through ensureQueryData, and
  // that list is cached for 12 hours — so without this splice the redirect at
  // the end of onboarding lands on a store the cache still believes has no
  // upload, and bounces the user straight back here.
  useEffect(() => {
    if (polledStore?.last_uploaded_at == null) {
      return;
    }

    queryClient.setQueryData(['stores'], (cachedStores: Store[] | undefined) => {
      if (!cachedStores || cachedStores.length === 0) {
        return [polledStore];
      }

      const updatedStores = cachedStores.map((s) => (s.id === polledStore.id ? polledStore : s));
      const foundStore = cachedStores.some((s) => s.id === polledStore.id);

      return foundStore ? updatedStores : [...updatedStores, polledStore];
    });
  }, [polledStore, queryClient]);

  // Start trial — create checkout session
  const handleStartTrial = useCallback(async () => {
    setSubmitting(true);
    try {
      const response = await stripeService.createCheckoutSession({
        owner_first_name: user.given_name ?? 'First',
        owner_last_name: user.family_name ?? 'Last',
      });
      window.location.href = response.session_url;
    } catch (error) {
      setSubmitting(false);
      addErrorAlert((error as Error).message, { cause: error });
    }
  }, [stripeService, user, addErrorAlert]);

  const handleSaveName = useCallback(
    async (newName: string) => {
      if (!store) {
        return;
      }

      setSavingName(true);
      try {
        await updateStore({ ...store, name: newName });
        setNameJustConfirmed(true);
      } finally {
        setSavingName(false);
      }
    },
    [store, updateStore]
  );

  const userEmail = user.email ?? '';

  // Avoid flashing Step 2 while redirecting on the bare /onboard route
  if (!storeIdParam && storesLoading) {
    return (
      <Page title="Get started with Orderbot">
        <div className="flex justify-center pt-12">
          <Spinner />
        </div>
      </Page>
    );
  }

  return (
    <Page title="Get started with Orderbot">
      {/* Gated on enabled for the same reason StoreLayoutComponent is: the
          backend's authorizeStoreAccess refuses $connect for a disabled store,
          so mounting it earlier would just retry-loop against a 403. */}
      {isWaitingForUpload && socketCoversUpdates && storeIdParam != null && (
        <StoreWebSocketProvider storeId={storeIdParam}>
          <OnboardStoreEvents storeId={storeIdParam} />
        </StoreWebSocketProvider>
      )}
      <div className="max-w-xl w-full pt-6 mx-auto">
        {/* Step 1 — Account created */}
        <div className="flex gap-4">
          <div className="flex flex-col items-center">
            <StepCircle state={steps.step1} number={1} />
            <StepConnector state={steps.step1} />
          </div>
          <div className="pt-1 pb-4">
            <div className="font-semibold text-gray-500">Account created</div>
            <div className="text-sm text-gray-500">Signed in as {userEmail}</div>
          </div>
        </div>

        {/* Step 2 — Start your free trial */}
        <div className="flex gap-4">
          <div className="flex flex-col items-center">
            <StepCircle state={steps.step2} number={2} />
            <StepConnector state={steps.step2} />
          </div>
          <div className="pt-1 pb-4 flex-1">
            <div
              className={`font-semibold ${steps.step2 === 'active' ? 'text-gray-900 text-xl' : 'text-gray-500'}`}
            >
              Start your free trial
            </div>
            {steps.step2 === 'complete' && (
              <div className="text-sm text-gray-500">
                60-day free trial — we'll email your plan and price before it ends
              </div>
            )}
            {steps.step2 === 'active' && (
              <PaymentStep store={store} onStartTrial={handleStartTrial} submitting={submitting} />
            )}
          </div>
        </div>

        {/* Step 3 — Tell us your store's name */}
        <div className="flex gap-4">
          <div className="flex flex-col items-center">
            <StepCircle state={steps.step3} number={3} />
            <StepConnector state={steps.step3} />
          </div>
          <div className="pt-1 pb-4 flex-1">
            <div
              className={`font-semibold ${steps.step3 === 'active' ? 'text-gray-900 text-xl' : 'text-gray-500'}`}
            >
              Tell us your store's name
            </div>
            {steps.step3 === 'active' && store && (
              <NameStep store={store} onSave={handleSaveName} submitting={savingName} />
            )}
          </div>
        </div>

        {/* Step 4 — Import your data */}
        <div className="flex gap-4">
          <div className="flex flex-col items-center">
            <StepCircle state={steps.step4} number={4} />
            <StepConnector state={steps.step4} />
          </div>
          <div className="pt-1 pb-4 flex-1">
            <div
              className={`font-semibold ${steps.step4 === 'active' ? 'text-gray-900 text-xl' : 'text-gray-500'}`}
            >
              Import your data
            </div>
            {steps.step4 === 'active' && <ImportStep userEmail={userEmail} />}
          </div>
        </div>

        {/* Step 5 — Start using Orderbot */}
        <div className="flex gap-4">
          <div className="flex flex-col items-center">
            <StepCircle state={steps.step5} number={5} />
          </div>
          <div className="pt-1 flex-1">
            <div
              className={`font-semibold ${steps.step5 === 'active' ? 'text-gray-900 text-xl' : 'text-gray-500'}`}
            >
              Start using Orderbot
            </div>
            {steps.step5 === 'active' && store && <RedirectStep storeId={store.id} />}
          </div>
        </div>
      </div>
    </Page>
  );
}
